import {Form, Link, useRouteLoaderData} from 'react-router'
import {useEffect, useRef, useState} from 'react'
import type {loader} from '../../../../root'
import {ovidTrack} from '@product/ovid/utils/analytics'
import {MuseFrame} from '@product/ovid/components/MuseFrame/MuseFrame'
import styles from './UserBlock.module.scss'

/**
 * UserBlock — the signed-in avatar + dropdown, ported from narcis's UserBlock
 * (components/UserBlock/userblock.tsx) and restyled to the House tokens.
 *
 * Signed out: unchanged "Enter" link (Header's previous anonymous branch).
 * Signed in: the header ambient before the avatar — her chosen muse (thumb + name), or a plain
 * "Your muse" ghost link when none is chosen. The avatar's own dropdown carries the permanent doors:
 * "Your muse" (`/muse`, where she chooses and where her own muses live) and "Make a muse of your own"
 * (the wall's intake, `/?intake=1`).
 */
export function UserBlock() {
    const loaderData = useRouteLoaderData<typeof loader>('root')
    const {user} = loaderData ?? {}
    // root.tsx resolves the chosen muse once (roster, or one of her own: `slug` is the muse key);
    // narcis/quies never populate it.
    const ovidChosenMuse = (loaderData as any)?.ovidChosenMuse as {slug: string; name: string; thumb: string | null} | null ?? null

    const [isOpen, setIsOpen] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    // Close dropdown on outside click — same contract as narcis's UserBlock.
    useEffect(() => {
        if (!isOpen) return
        function handleOutsideClick(e: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [isOpen])

    if (!user) {
        return (
            <Link className="ov-btn ov-btn--secondary" to="/auth/login">Enter</Link>
        )
    }

    const initial = user.email ? user.email[0].toLowerCase() : '?'

    return (
        <>
            {ovidChosenMuse ? (
                <Link
                    to="/muse"
                    className={styles.ambientLink}
                    aria-label={`Your muse — ${ovidChosenMuse.name}`}
                    onClick={() => ovidTrack('muse_nav_click', {source: 'userblock'})}
                >
                    <MuseFrame
                        src={ovidChosenMuse.thumb ?? ''}
                        alt=""
                        className={styles.ambientThumb}
                    />
                    {ovidChosenMuse.name}
                </Link>
            ) : (
                <Link
                    to="/muse"
                    className={styles.ambientLink}
                    aria-label="Your muse — choose her"
                    onClick={() => ovidTrack('muse_nav_click', {source: 'userblock'})}
                >
                    Your muse
                </Link>
            )}
            <div className={styles.wrap} ref={wrapperRef}>
                <button
                    type="button"
                    className={styles.avatar}
                    aria-label="Account menu"
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen((v) => !v)}
                >
                    {initial}.
                </button>

                {isOpen && (
                    <div className={styles.dropdown}>
                        <div className={styles.who}>{user.email}</div>

                        {/* The permanent doors, never conditioned on whether a muse is chosen yet. */}
                        <Link
                            to="/muse"
                            className={styles.item}
                            onClick={() => {
                                setIsOpen(false)
                                ovidTrack('muse_nav_click', {source: 'userblock'})
                            }}
                        >
                            Your muse
                            <span className={styles.itemTail}>
                                {ovidChosenMuse ? ` · ${ovidChosenMuse.name}` : ' · choose her →'}
                            </span>
                        </Link>
                        <Link
                            to="/?intake=1"
                            className={styles.item}
                            onClick={() => {
                                setIsOpen(false)
                                ovidTrack('model_cta_click', {source: 'userblock_dropdown'})
                            }}
                        >
                            Make a muse of your own
                            <span className={styles.itemTail}> →</span>
                        </Link>

                        <Form method="post" action="/auth/logout">
                            <button type="submit" className={styles.item}>
                                Sign out
                            </button>
                        </Form>
                    </div>
                )}
            </div>
        </>
    )
}

export default UserBlock
